import React from 'react';
import EmojiPicker from '../../EmojiPicker';

const CategoriesTab = ({
  activeTab,
  bulkCategoryActionType,
  bulkCategoryColor,
  bulkStartPosition,
  categories,
  categoryPdfs,
  categorySearchTerm,
  deselectAllCategories,
  draggedCategoryIndex,
  duplicateCategoryAction,
  exportCategoryData,
  formatDate,
  getCategoryCompletionRate,
  getCategorySortIcon,
  getFilteredCategories,
  handleAddCategory,
  handleBulkCategoryAction,
  handleCategoryDragStart,
  handleCategoryDrop,
  handleCategorySort,
  handleDeleteCategory,
  handleOpenPdfModal,
  newCategory,
  openPdfListModal,
  selectAllCategories,
  selectedCategories,
  selectedModuleCountFilter,
  selectedStatusFilter,
  setBulkCategoryActionType,
  setBulkCategoryColor,
  setBulkStartPosition,
  setCategorySearchTerm,
  setDraggedCategoryIndex,
  setEditingCategory,
  setNewCategory,
  setSelectedModuleCountFilter,
  setSelectedStatusFilter,
  setShowAddCategory,
  setShowEmptyCategories,
  showAddCategory,
  showCategoryAnalyticsModal,
  showEmptyCategories,
  toggleCategorySelection,
  toggleCategoryStatusAction
}) => {
  return (
    <>
{activeTab === 'categories' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">Kategorien verwalten</h2>
                    <p className="text-slate-600 mt-1">Verwalten Sie alle Kategorien mit erweiterten Funktionen</p>
                  </div>
                  <button
                    onClick={() => setShowAddCategory(!showAddCategory)}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition-colors shadow-lg hover:shadow-xl"
                  >
                    {showAddCategory ? 'Abbrechen' : 'Neue Kategorie'}
                  </button>
                </div>

                {/* Phase 1: Search and Filter Bar for Categories */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search Input */}
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Kategorien durchsuchen..."
                        value={categorySearchTerm || ''}
                        onChange={(e) => setCategorySearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    
                    {/* Status Filter */}
                    <select
                      value={selectedStatusFilter || ''}
                      onChange={(e) => setSelectedStatusFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Alle Status</option>
                      <option value="active">Aktiv</option>
                      <option value="inactive">Inaktiv</option>
                    </select>
                    
                    {/* Module Count Filter */}
                    <select
                      value={selectedModuleCountFilter || ''}
                      onChange={(e) => setSelectedModuleCountFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Alle Kategorien</option>
                      <option value="populated">Mit Modulen</option>
                      <option value="empty">Leer</option>
                    </select>
                    
                    {/* Additional Options */}
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input
                          type="checkbox"
                          checked={showEmptyCategories}
                          onChange={(e) => setShowEmptyCategories(e.target.checked)}
                          className="rounded border-slate-300 text-green-600 focus:ring-green-500"
                        />
                        Leere anzeigen
                      </label>
                    </div>
                  </div>
                  
                  {/* Results Summary */}
                  <div className="mt-3 text-sm text-gray-600">
                    {getFilteredCategories().length} von {categories.length} Kategorien angezeigt
                  </div>
                </div>

                {/* Phase 2: Bulk Actions Bar for Categories */}
                {selectedCategories.size > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-green-900">
                          {selectedCategories.size} Kategorien ausgewählt
                        </span>
                        <button
                          onClick={deselectAllCategories}
                          className="text-green-600 hover:text-green-800 text-sm underline"
                        >
                          Alle abwählen
                        </button>
                      </div>
                      <div className="flex items-center space-x-2">
                        <select
                          value={bulkCategoryActionType || ''}
                          onChange={(e) => setBulkCategoryActionType(e.target.value)}
                          className="px-3 py-1 border border-green-300 rounded-lg text-sm"
                        >
                          <option value="">Aktion wählen...</option>
                          <option value="activate">Aktivieren</option>
                          <option value="deactivate">Deaktivieren</option>
                          <option value="reorder">Position ändern</option>
                          <option value="colorChange">Farbe ändern</option>
                          <option value="delete">Löschen</option>
                        </select>
                        
                        {bulkCategoryActionType === 'reorder' && (
                          <input
                            type="number"
                            placeholder="Start-Position"
                            value={bulkStartPosition || ''}
                            onChange={(e) => setBulkStartPosition(parseInt(e.target.value) || 1)}
                            className="px-3 py-1 border border-green-300 rounded-lg text-sm w-24"
                            min="1"
                          />
                        )}
                        
                        {bulkCategoryActionType === 'colorChange' && (
                          <input
                            type="color"
                            value={bulkCategoryColor || '#3B82F6'}
                            onChange={(e) => setBulkCategoryColor(e.target.value)}
                            className="w-8 h-8 border border-green-300 rounded cursor-pointer"
                          />
                        )}
                        
                        <button
                          onClick={handleBulkCategoryAction}
                          disabled={!bulkCategoryActionType}
                          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-1 rounded-lg text-sm font-medium transition-colors"
                        >
                          Ausführen
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showAddCategory && (
                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                    <form onSubmit={handleAddCategory} className="space-y-4">
                      <div className="flex space-x-4">
                        <input
                          type="text"
                          placeholder="Titel"
                          value={newCategory.title}
                          onChange={(e) => setNewCategory({...newCategory, title: e.target.value})}
                          className="flex-1 px-4 py-3 border border-green-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          required
                        />
                        <EmojiPicker
                          currentEmoji={newCategory.icon}
                          onSelect={(emoji) => setNewCategory({...newCategory, icon: emoji})}
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Beschreibung"
                        value={newCategory.description}
                        onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                        className="w-full px-4 py-3 border border-green-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Farbe</label>
                          <input
                            type="color"
                            value={newCategory.color}
                            onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                            className="w-full h-12 border border-green-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>

                      {/* Simplified Role Targeting Section */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-blue-900 mb-3">🎯 Zielgruppe festlegen</h4>
                        <div className="mb-4">
                          <div className="space-y-2">
                            <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer bg-white">
                              <input
                                type="radio"
                                name="target_role"
                                value="vertriebler"
                                checked={!newCategory.target_roles?.includes('teamleiter')}
                                onChange={() => setNewCategory({ ...newCategory, target_roles: ['vertriebler'], visibility_mode: 'cascade' })}
                                className="mt-0.5 text-blue-600 focus:ring-blue-500"
                              />
                              <div>
                                <span className="font-medium text-gray-900">💼 Vertriebler-Schulung</span>
                                <p className="text-xs text-gray-600 mt-1">
                                  <strong>Pflicht für:</strong> Vertriebler &nbsp;·&nbsp; <strong>Mitsehen:</strong> Teamleiter, Vertriebsleiter, Admin
                                </p>
                              </div>
                            </label>
                            <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer bg-white">
                              <input
                                type="radio"
                                name="target_role"
                                value="teamleiter"
                                checked={newCategory.target_roles?.includes('teamleiter') || false}
                                onChange={() => setNewCategory({ ...newCategory, target_roles: ['teamleiter'], visibility_mode: 'cascade' })}
                                className="mt-0.5 text-blue-600 focus:ring-blue-500"
                              />
                              <div>
                                <span className="font-medium text-gray-900">👥 Teamleiter-Schulung</span>
                                <p className="text-xs text-gray-600 mt-1">
                                  <strong>Pflicht für:</strong> Teamleiter &nbsp;·&nbsp; <strong>Mitsehen:</strong> Vertriebsleiter, Admin
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-start space-x-2">
                            <svg className="w-4 h-4 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-xs font-medium text-green-800">Automatische Hierarchie</p>
                              <p className="text-xs text-green-700 mt-1">
                                Vertriebsleiter und Admin haben automatisch Zugriff auf alle Kategorien und Module.
                                Höhere Rollen können Inhalte niedrigerer Rollen einsehen, müssen sie aber nicht abschließen.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition-colors shadow-lg hover:shadow-xl"
                        >
                          Kategorie hinzufügen
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {showAddCategory && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Live-Vorschau:</h3>
                    <div className="bg-white rounded-xl shadow-lg p-4 max-w-md">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-lg"
                          style={{ 
                            backgroundColor: newCategory.color,
                            boxShadow: `0 4px 15px -4px ${newCategory.color}40`
                          }}
                        >
                          {newCategory.icon || newCategory.title[0] || '?'}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">
                            {newCategory.title || 'Kategorie-Titel'}
                          </h4>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {newCategory.description || 'Kategorie-Beschreibung'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phase 1: Enhanced Category Table Layout */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={selectedCategories.size === getFilteredCategories().length && getFilteredCategories().length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  selectAllCategories();
                                } else {
                                  deselectAllCategories();
                                }
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleCategorySort('title')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Kategorie</span>
                              {getCategorySortIcon('title')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleCategorySort('modules_count')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Module</span>
                              {getCategorySortIcon('modules_count')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleCategorySort('pdf_count')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>PDFs</span>
                              {getCategorySortIcon('pdf_count')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleCategorySort('order')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Position</span>
                              {getCategorySortIcon('order')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleCategorySort('created_at')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Erstellt</span>
                              {getCategorySortIcon('created_at')}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleCategorySort('completion_rate')}
                            title="Abschlussrate der Nutzer"
                          >
                            <div className="flex items-center space-x-1">
                              <span>Analytics</span>
                              {getCategorySortIcon('completion_rate')}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Aktionen
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getFilteredCategories().map((category, index) => (
                          <tr
                            key={category.id}
                            draggable
                            onDragStart={(e) => handleCategoryDragStart(e, index)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handleCategoryDrop(e, index)}
                            onDragEnd={() => setDraggedCategoryIndex(null)}
                            className={`hover:bg-gray-50 transition-colors ${
                              selectedCategories.has(category.id) ? 'bg-green-50' :
                              draggedCategoryIndex === index ? 'opacity-40 bg-blue-50' : ''
                            }`}
                            style={{ cursor: 'grab' }}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedCategories.has(category.id)}
                                onChange={() => toggleCategorySelection(category.id)}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="cursor-move text-gray-400 hover:text-gray-600 mr-3">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                  </svg>
                                </div>
                                <div 
                                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm mr-3"
                                  style={{ backgroundColor: category.color }}
                                >
                                  {category.icon || category.title[0]}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{category.title}</div>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {category.target_roles?.includes('teamleiter') ? (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">👥 Teamleiter</span>
                                    ) : (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">💼 Vertriebler</span>
                                    )}
                                    {category.description && <div className="text-xs text-gray-400 truncate max-w-xs">{category.description}</div>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">{category.modules_count}</span>
                                {category.modules_count === 0 && (
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                    Leer
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{categoryPdfs[category.id]?.length || 0}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPdfModal(category);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                  title="PDF hochladen"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                                {(categoryPdfs[category.id]?.length || 0) > 0 && (
                                  <button
                                    onClick={() => openPdfListModal(category)}
                                    className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                                    title="PDFs verwalten"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {category.order}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(category.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {category.is_active ? '● Aktiv' : '● Inaktiv'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${getCategoryCompletionRate(category.id)}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 font-medium">{getCategoryCompletionRate(category.id)}%</span>
                                <button
                                  onClick={() => showCategoryAnalyticsModal(category)}
                                  className="text-green-600 hover:text-green-800 p-1 rounded"
                                  title="Detaillierte Analytics"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                {/* Phase 2 & 3: Enhanced actions */}
                                <button
                                  onClick={() => duplicateCategoryAction(category)}
                                  className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50"
                                  title="Kategorie duplizieren"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => showCategoryAnalyticsModal(category)}
                                  className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50"
                                  title="Category Analytics"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => exportCategoryData(category)}
                                  className="text-cyan-600 hover:text-cyan-800 p-1 rounded hover:bg-cyan-50"
                                  title="Daten exportieren"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingCategory(category.id);
                                    setNewCategory({
                                      title: category.title,
                                      description: category.description,
                                      color: category.color || '#3B82F6',
                                      icon: category.icon || '',
                                      order: category.order || 0,
                                      target_roles: category.target_roles || [],
                                      is_mandatory_for_roles: category.is_mandatory_for_roles || [],
                                      visibility_mode: category.visibility_mode || 'strict'
                                    });
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                  title="Kategorie bearbeiten"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => toggleCategoryStatusAction(category)}
                                  className={`p-1 rounded transition-colors ${
                                    category.is_active 
                                      ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50' 
                                      : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                                  }`}
                                  title={category.is_active ? 'Kategorie deaktivieren' : 'Kategorie aktivieren'}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {category.is_active ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636" />
                                    ) : (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    )}
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteCategory(category)}
                                  className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                  title="Kategorie löschen"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Empty State */}
                  {getFilteredCategories().length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-4xl mb-4">📂</div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Kategorien gefunden</h3>
                      <p className="text-gray-500">
                        {categorySearchTerm || selectedStatusFilter || selectedModuleCountFilter 
                          ? 'Versuchen Sie andere Suchkriterien.'
                          : 'Erstellen Sie Ihre erste Kategorie.'
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
    </>
  );
};

export default CategoriesTab;
